import React from 'react';
import { LogoLoader } from '@/components/ui/logo-loader';

// Default button size for sidebar icons
const DEFAULT_BUTTON_SIZE = 36;

// Container component for right sidebar
interface RightSidebarProps {
  children: React.ReactNode;
  className?: string;
}

export const RightSidebar: React.FC<RightSidebarProps> = ({
  children,
  className = ''
}) => {
  return (
    <div 
      className={`flex flex-col items-center gap-3 pt-4 w-20 ${className}`}
    >
      {children}
    </div>
  );
};

// Group component for label + button(s)
interface SidebarButtonGroupProps {
  label?: string;
  children: React.ReactNode;
  className?: string;
  hideLabel?: boolean;
}

export const SidebarButtonGroup: React.FC<SidebarButtonGroupProps> = ({
  label,
  children,
  className = '',
  hideLabel = false
}) => {
  return (
    <div className={`flex flex-col items-center gap-1 ${className}`}>
      {label && !hideLabel && (
        <span 
          className="text-sm font-medium text-black text-center"
          style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
        >
          {label}
        </span>
      )}
      {children}
    </div>
  );
};

// Icon button component with consistent styling
interface SidebarIconButtonProps {
  icon: string;
  alt: string;
  onClick?: () => void;
  disabled?: boolean;
  size?: number;
  title?: string;
  isLoading?: boolean;
  badge?: boolean;
  className?: string;
}

export const SidebarIconButton: React.FC<SidebarIconButtonProps> = ({
  icon,
  alt,
  onClick,
  disabled = false,
  size = DEFAULT_BUTTON_SIZE,
  title,
  isLoading = false,
  badge = false,
  className = ''
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`hover:opacity-70 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed relative ${className}`}
      title={title}
    >
      {isLoading ? (
        <LogoLoader size="sm" />
      ) : (
        <img 
          src={icon} 
          alt={alt}
          style={{ width: size, height: size }}
        />
      )}
      {badge && !isLoading && (
        <span className="absolute -top-1 -right-1 h-2 w-2 bg-blue-500 rounded-full"></span>
      )}
    </button>
  );
};

// Row component for buttons displayed side by side (e.g., zoom +/-)
interface SidebarButtonRowProps {
  children: React.ReactNode;
  className?: string;
}

export const SidebarButtonRow: React.FC<SidebarButtonRowProps> = ({
  children,
  className = ''
}) => {
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {children}
    </div>
  );
};
